import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_AUDIO_FROM_MEDIA } from '../graphql/queries';

const AudioContext = createContext();

export const useAudio = () => {
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error('useAudio must be used within AudioProvider');
    }
    return context;
};

export const AudioProvider = ({ children }) => {
    const audioRef = useRef(null);

    // Get base URL - empty in development, set in production
    const getAssetUrl = (path) => {
        const baseUrl = process.env.PUBLIC_URL || '';
        return baseUrl + path;
    };

    // Fetch audio files from WordPress Media Library
    console.log('🎵 AudioContext: Fetching from WordPress Media Library...');
    const { data: audioData, loading: audioLoading, error: audioError } = useQuery(GET_AUDIO_FROM_MEDIA, {
        variables: { first: 50 },
        fetchPolicy: 'network-only' // Force fresh data, no cache
    });

    // Default fallback tracks (in case WordPress fetch fails)
    const fallbackPlaylist = [
        {
            id: 1,
            title: 'Random Acoustic',
            artist: 'Senior FM',
            url: getAssetUrl('/assets/audio/random-acoustic.mp3'),
            thumbnail: getAssetUrl('/assets/img/600x600bb-14.jpg')
        },
        {
            id: 2,
            title: 'Random Acoustic 1',
            artist: 'Senior FM',
            url: getAssetUrl('/assets/audio/random-acoustic-1.mp3'),
            thumbnail: getAssetUrl('/assets/img/600x600bb-14.jpg')
        }
    ];

    // Transform WordPress Media Library data to playlist format
    const transformAudioTracks = (mediaItems) => {
        if (!mediaItems || mediaItems.length === 0) {
            console.log('No media items provided, using fallback');
            return fallbackPlaylist;
        }

        console.log('Total media items fetched:', mediaItems.length);

        // Filter only audio files and transform
        const transformed = mediaItems
            .filter(item => {
                const isAudio = item.mimeType && item.mimeType.startsWith('audio/');
                console.log(`File: ${item.title}, MIME: ${item.mimeType}, Is Audio: ${isAudio}`);
                return isAudio;
            })
            .map((item) => {
                console.log('✅ Processing audio file:', item.title);
                console.log('   Audio URL:', item.mediaItemUrl);

                // Extract artist from description (format: "Artist Name - Song Title" or just use title)
                const description = item.description || '';
                const artist = description.split('-')[0]?.trim() || 'Senior FM';

                return {
                    id: item.databaseId,
                    title: item.title,
                    artist: artist,
                    album: '',
                    url: item.mediaItemUrl,
                    thumbnail: getAssetUrl('/assets/img/600x600bb-14.jpg'), // Default thumbnail
                    duration: null,
                    mimeType: item.mimeType,
                    fileSize: item.fileSize
                };
            })
            .filter(track => {
                const hasUrl = !!track.url;
                if (!hasUrl) {
                    console.log('⚠️ Skipping track (no audio URL):', track.title);
                }
                return hasUrl;
            });

        console.log('Audio files after filtering:', transformed.length);

        // If no valid tracks after filtering, return fallback
        if (transformed.length === 0) {
            console.log('No valid audio files found in WordPress media, using fallback');
            return fallbackPlaylist;
        }

        return transformed;
    };

    // State for playlist and playback
    const [playlist, setPlaylist] = useState(fallbackPlaylist);
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(0.7);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(audioLoading);

    // Update playlist when WordPress data loads
    useEffect(() => {
        if (audioData?.mediaItems?.nodes) {
            const wpMedia = audioData.mediaItems.nodes;
            console.log('Raw WordPress media items:', wpMedia);
            console.log('Number of audio files from WP:', wpMedia.length);

            const transformedTracks = transformAudioTracks(wpMedia);
            console.log('Transformed tracks:', transformedTracks);

            if (transformedTracks.length > 0 && transformedTracks !== fallbackPlaylist) {
                setPlaylist(transformedTracks);
                console.log('✅ Audio files loaded from WordPress Media Library:', transformedTracks.length);
            } else {
                console.log('⚠️ Using fallback playlist - no valid WordPress audio files found');
            }
        } else if (audioData && !audioData.mediaItems) {
            console.log('⚠️ No mediaItems field in GraphQL response');
            console.log('Response data:', audioData);
        }
    }, [audioData]);

    // Handle loading state
    useEffect(() => {
        setIsLoading(audioLoading);
    }, [audioLoading]);

    // Log errors
    useEffect(() => {
        if (audioError) {
            console.error('Error fetching audio tracks:', audioError);
            console.log('Using fallback playlist');
        }
    }, [audioError]);

    const currentTrack = playlist[currentTrackIndex];

    // Update audio src when track changes
    useEffect(() => {
        if (audioRef.current && currentTrack?.url) {
            console.log('🎵 Setting audio source:', currentTrack.title);
            audioRef.current.src = currentTrack.url;
            audioRef.current.load();

            // Auto-play if should be playing
            if (isPlaying) {
                audioRef.current.play().catch(error => {
                    console.error('Auto-play error:', error);
                    setIsPlaying(false);
                });
            }
        }
    }, [currentTrackIndex, currentTrack]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
        }
    }, [volume]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const updateTime = () => setCurrentTime(audio.currentTime);
        const updateDuration = () => setDuration(audio.duration);
        const handleEnded = () => {
            if (currentTrackIndex < playlist.length - 1) {
                playNext();
            } else {
                setIsPlaying(false);
            }
        };
        const handleLoadStart = () => setIsLoading(true);
        const handleCanPlay = () => {
            setIsLoading(false);
            // Auto-play when track is ready and should be playing
            if (isPlaying && audio.paused) {
                audio.play().catch(error => console.error('Auto-play error:', error));
            }
        };

        audio.addEventListener('timeupdate', updateTime);
        audio.addEventListener('loadedmetadata', updateDuration);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('loadstart', handleLoadStart);
        audio.addEventListener('canplay', handleCanPlay);

        // Load and play if should be playing
        if (isPlaying) {
            audio.load();
        }

        return () => {
            audio.removeEventListener('timeupdate', updateTime);
            audio.removeEventListener('loadedmetadata', updateDuration);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('loadstart', handleLoadStart);
            audio.removeEventListener('canplay', handleCanPlay);
        };
    }, [currentTrackIndex, isPlaying]);

    const play = () => {
        if (audioRef.current) {
            audioRef.current.play()
                .then(() => setIsPlaying(true))
                .catch(error => console.error('Play error:', error));
        }
    };

    const pause = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };

    const togglePlayPause = () => {
        if (isPlaying) {
            pause();
        } else {
            play();
        }
    };

    const playNext = () => {
        if (currentTrackIndex < playlist.length - 1) {
            setCurrentTrackIndex(currentTrackIndex + 1);
            setIsPlaying(true);
        }
    };

    const playPrevious = () => {
        if (currentTrackIndex > 0) {
            setCurrentTrackIndex(currentTrackIndex - 1);
            setIsPlaying(true);
        } else {
            seek(0);
        }
    };

    const playTrack = (index) => {
        setCurrentTrackIndex(index);
        setIsPlaying(true);
    };

    const seek = (time) => {
        if (audioRef.current) {
            audioRef.current.currentTime = time;
            setCurrentTime(time);
        }
    };

    const changeVolume = (newVolume) => {
        setVolume(newVolume);
    };

    const value = {
        audioRef,
        playlist,
        currentTrack,
        currentTrackIndex,
        isPlaying,
        volume,
        currentTime,
        duration,
        isLoading,
        play,
        pause,
        togglePlayPause,
        playNext,
        playPrevious,
        playTrack,
        seek,
        changeVolume
    };

    return (
        <AudioContext.Provider value={value}>
            <audio
                ref={audioRef}
                src={currentTrack?.url || ''}
                preload="auto"
                style={{ display: 'none' }}
            />
            {children}
        </AudioContext.Provider>
    );
};
